import Decimal from 'decimal.js';

// Configure Decimal.js for financial calculations
Decimal.set({
  precision: 20,
  rounding: Decimal.ROUND_HALF_UP
});

/**
 * Default scale for monetary values (2 decimal places for NIS)
 */
const DEFAULT_SCALE = 2;

/**
 * Default scale for percentage values
 */
const PERCENT_SCALE = 2;

/**
 * Money class for safe arithmetic operations on monetary values.
 * Uses decimal.js internally to avoid floating-point precision issues.
 *
 * @example
 * const price = Money.from('1000.50');
 * const tax = price.percentOf(17);
 * const total = price.add(tax);
 * console.log(total.toString()); // '1170.59'
 */
export class Money {
  private readonly value: Decimal;
  private readonly scale: number;

  private constructor(value: Decimal, scale: number = DEFAULT_SCALE) {
    this.value = value;
    this.scale = scale;
  }

  /**
   * Create a Money instance from a string or number
   */
  static from(value: string | number | Money, scale: number = DEFAULT_SCALE): Money {
    if (value instanceof Money) {
      return value;
    }
    const decimal = new Decimal(value);
    return new Money(decimal, scale);
  }

  /**
   * Create a Money instance representing zero
   */
  static zero(scale: number = DEFAULT_SCALE): Money {
    return new Money(new Decimal(0), scale);
  }

  /**
   * Sum an array of Money values
   */
  static sum(values: Money[], scale: number = DEFAULT_SCALE): Money {
    if (values.length === 0) {
      return Money.zero(scale);
    }
    const total = values.reduce((acc, val) => acc.plus(val.value), new Decimal(0));
    return new Money(total, scale);
  }

  /**
   * Get the maximum of multiple Money values
   */
  static max(...values: Money[]): Money {
    if (values.length === 0) {
      throw new Error('Cannot get max of empty array');
    }
    return values.reduce((max, val) => (val.gt(max) ? val : max));
  }

  /**
   * Get the minimum of multiple Money values
   */
  static min(...values: Money[]): Money {
    if (values.length === 0) {
      throw new Error('Cannot get min of empty array');
    }
    return values.reduce((min, val) => (val.lt(min) ? val : min));
  }

  /**
   * Add another Money value
   */
  add(other: Money | string | number): Money {
    const otherMoney = Money.from(other, this.scale);
    return new Money(this.value.plus(otherMoney.value), this.scale);
  }

  /**
   * Subtract another Money value
   */
  subtract(other: Money | string | number): Money {
    const otherMoney = Money.from(other, this.scale);
    return new Money(this.value.minus(otherMoney.value), this.scale);
  }

  /**
   * Alias for subtract
   */
  minus(other: Money | string | number): Money {
    return this.subtract(other);
  }

  /**
   * Multiply by a number (not Money - multiplication of two money values doesn't make sense)
   */
  multiply(factor: number | string): Money {
    const decimal = new Decimal(factor);
    return new Money(this.value.times(decimal), this.scale);
  }

  /**
   * Divide by a number
   */
  divide(divisor: number | string): Money {
    const decimal = new Decimal(divisor);
    if (decimal.isZero()) {
      throw new Error('Cannot divide by zero');
    }
    return new Money(this.value.dividedBy(decimal), this.scale);
  }

  /**
   * Calculate a percentage of this money value
   * @param percent - The percentage (e.g., 7 for 7%)
   * @returns The calculated amount
   *
   * @example
   * Money.from(1000).percentOf(7) // => Money representing 70
   */
  percentOf(percent: number | string): Money {
    const decimal = new Decimal(percent);
    return new Money(this.value.times(decimal).dividedBy(100), this.scale);
  }

  /**
   * Calculate what percentage this money is of another value
   * @param total - The total amount to compare against
   * @returns The percentage as a number (e.g., 7 for 7%)
   *
   * @example
   * Money.from(70).toPercentOf(Money.from(1000)) // => 7
   */
  toPercentOf(total: Money | string | number): number {
    const totalMoney = Money.from(total, this.scale);
    if (totalMoney.isZero()) {
      return 0;
    }
    const percent = this.value.dividedBy(totalMoney.value).times(100);
    return percent.toDecimalPlaces(PERCENT_SCALE, Decimal.ROUND_HALF_UP).toNumber();
  }

  /**
   * Check if equal to another Money value
   */
  equals(other: Money | string | number): boolean {
    const otherMoney = Money.from(other, this.scale);
    return this.value.equals(otherMoney.value);
  }

  /**
   * Check if greater than another Money value
   */
  gt(other: Money | string | number): boolean {
    const otherMoney = Money.from(other, this.scale);
    return this.value.greaterThan(otherMoney.value);
  }

  /**
   * Check if greater than or equal to another Money value
   */
  gte(other: Money | string | number): boolean {
    const otherMoney = Money.from(other, this.scale);
    return this.value.greaterThanOrEqualTo(otherMoney.value);
  }

  /**
   * Check if less than another Money value
   */
  lt(other: Money | string | number): boolean {
    const otherMoney = Money.from(other, this.scale);
    return this.value.lessThan(otherMoney.value);
  }

  /**
   * Check if less than or equal to another Money value
   */
  lte(other: Money | string | number): boolean {
    const otherMoney = Money.from(other, this.scale);
    return this.value.lessThanOrEqualTo(otherMoney.value);
  }

  /**
   * Check if this value is zero
   */
  isZero(): boolean {
    return this.value.isZero();
  }

  /**
   * Check if this value is positive (> 0)
   */
  isPositive(): boolean {
    return this.value.isPositive() && !this.value.isZero();
  }

  /**
   * Check if this value is negative (< 0)
   */
  isNegative(): boolean {
    return this.value.isNegative();
  }

  /**
   * Get the absolute value
   */
  abs(): Money {
    return new Money(this.value.abs(), this.scale);
  }

  /**
   * Negate the value
   */
  negate(): Money {
    return new Money(this.value.negated(), this.scale);
  }

  /**
   * Round to the configured scale
   */
  round(): Money {
    return new Money(this.value.toDecimalPlaces(this.scale, Decimal.ROUND_HALF_UP), this.scale);
  }

  /**
   * Clamp value between min and max
   */
  clamp(min: Money | string | number, max: Money | string | number): Money {
    const minMoney = Money.from(min, this.scale);
    const maxMoney = Money.from(max, this.scale);

    if (this.lt(minMoney)) return minMoney;
    if (this.gt(maxMoney)) return maxMoney;
    return this;
  }

  /**
   * Convert to a JavaScript number
   * Warning: May lose precision for very large values
   */
  toNumber(): number {
    return this.round().value.toNumber();
  }

  /**
   * Convert to a string with the configured scale
   * Suitable for database storage and API responses
   */
  toString(): string {
    return this.value.toDecimalPlaces(this.scale, Decimal.ROUND_HALF_UP).toFixed(this.scale);
  }

  /**
   * Format as NIS currency string
   * @param options - Formatting options; `compact` abbreviates to K/M (e.g. ₪1M, ₪250K)
   */
  formatNIS(options?: {symbol?: boolean; locale?: string; compact?: boolean}): string {
    const {symbol = true, compact = false} = options ?? {};
    // he-IL compact output wraps the suffix in bidi marks; en-US gives a clean "1M".
    const locale = options?.locale ?? (compact ? 'en-US' : 'he-IL');
    const num = this.toNumber();
    const isNegative = num < 0;
    const absNum = Math.abs(num);
    const isWholeNumber = Number.isInteger(absNum);
    const formatted = compact
      ? absNum.toLocaleString(locale, {notation: 'compact', maximumFractionDigits: 1})
      : absNum.toLocaleString(locale, {
          minimumFractionDigits: isWholeNumber ? 0 : this.scale,
          maximumFractionDigits: this.scale
        });
    if (!symbol) return isNegative ? `- ${formatted}` : formatted;
    return isNegative ? `- ₪${formatted}` : `₪${formatted}`;
  }

  /**
   * Alias for toString for JSON serialization
   */
  toJSON(): string {
    return this.toString();
  }
}

/**
 * Parse a money string into a Money instance
 * Convenience function for Money.from()
 */
export function parseMoney(value: string | number | Money | null | undefined): Money {
  if (value === null || value === undefined || value === '') {
    return Money.zero();
  }
  return Money.from(value);
}

/**
 * Format a Money value as NIS
 * Convenience function for Money.formatNIS()
 */
export function formatNIS(
  value: Money | string | number,
  options?: Parameters<Money['formatNIS']>[0]
): string {
  return Money.from(value).formatNIS(options);
}

/**
 * Sum an array of Money values
 * Convenience function for Money.sum()
 */
export function sumMoney(values: Money[]): Money {
  return Money.sum(values);
}

/**
 * Zero constant for convenience
 */
export const ZERO = Money.zero();

/**
 * A NIS amount as it is stored and transferred — bounded to the 12 integer digits every money
 * column allows (`numeric(14,2)`), so an oversized amount is a 400 rather than a PG overflow.
 */
export const MONEY_NIS_PATTERN = /^\d{1,12}(\.\d{1,2})?$/;

/**
 * Sanitize a user-typed string to a valid money input value.
 * Allows digits and at most one decimal point with up to 2 decimal places.
 * Strips all other characters (e.g. thousands separators typed by the user).
 */
export function sanitizeMoneyInput(input: string): string {
  const cleaned = input.replace(/[^\d.]/g, '');
  const dot = cleaned.indexOf('.');
  if (dot === -1) return cleaned;
  const intPart = cleaned.slice(0, dot) || '0';
  const decPart = cleaned
    .slice(dot + 1)
    .replace(/\D/g, '')
    .slice(0, 2);
  return `${intPart}.${decPart}`;
}
